'use client';
import IconChevronDown from '@mnd-frontend/icons/components/ChevronDown';
import { SIZING_2, SIZING_7, SPACING_3 } from '@mnd-frontend/ui/shared/tokens';
import { type MouseEvent, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  type CollapseItemInAccordion,
  type CollapsibleProps,
  isItemInAccordion,
} from './index.type';

const LabelButton = styled.button`
  font-size: ${SIZING_2};
  font-weight: inherit;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING_3};
  text-align: left;
  min-height: ${SIZING_7};
  width: 100%;
  padding: 0;
  line-height: 120%;
  cursor: pointer;
  border: none;
  background-color: transparent;

  .mnd-icon {
    color: inherit;
    flex-shrink: 0;
  
    .icon{
      transition: transform 200ms linear;

      &.rotate {
        transform: rotate(-180deg);
      }
    }
  }
`;

const COLLAPSE_SPEED = '300ms';
const CONTENT_Y_PADDING = 16;

const CollapsibleContent = styled.div<{ $maxHeight: number }>`
  font-size: inherit;
  font-weight: inherit;
  max-height: ${({ $maxHeight }) => $maxHeight + 'px'};
  padding-top: ${CONTENT_Y_PADDING}px;
  padding-bottom: ${CONTENT_Y_PADDING * 2}px;
  opacity: 1;
  transition:
    max-height ${COLLAPSE_SPEED} ease,
    padding-top ${COLLAPSE_SPEED} ease,
    padding-bottom ${COLLAPSE_SPEED} ease,
    opacity 100ms ease;

  &.collapsed {
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
    max-height: 0;
    overflow: hidden;
  }
`;

const Collapsible = (props: CollapsibleProps) => {
  const isAccordionItem = isItemInAccordion(props);
  const contentRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(!!(isAccordionItem && props.isExpandedViaParent));

  const { id, label, content } = props;

  const handleExpandClick = () => {
    if (isAccordionItem) {
      // Expansion status is controlled via prop from parent
      const { isExpandedViaParent } = props;
      setIsExpanded(isExpandedViaParent);
    } else {
      setIsExpanded(prev => !prev);
    }
  };

  const expand = (props as CollapseItemInAccordion).isExpandedViaParent ?? isExpanded;

  // When as a part of accordion, the content should not react to click event from the parent.
  const handlePropagation = (e: MouseEvent<HTMLDivElement>) => {
    if (isAccordionItem) {
      e.stopPropagation();
    }
  };

  // Actual element height + y-axis paddings.
  const totalContentHeight = (contentRef.current?.scrollHeight ?? 0) + CONTENT_Y_PADDING * 3;

  return (
    <>
      <LabelButton
        type="button"
        onClick={handleExpandClick}
        aria-expanded={isExpanded}
        aria-controls={id}
      >
        {label}
        <IconChevronDown className={expand ? 'icon rotate' : 'icon'} />
      </LabelButton>
      <CollapsibleContent
        ref={contentRef}
        $maxHeight={totalContentHeight}
        className={expand ? '' : 'collapsed'}
        onClickCapture={handlePropagation}
      >
        {content}
      </CollapsibleContent>
    </>
  );
};

export default Collapsible;
